<script lang="ts">
  import Icon from "./Icon.svelte";
  import Spinner from "./Spinner.svelte";
  import type { Snippet } from "svelte";
  import type { HTMLAnchorAttributes, HTMLButtonAttributes } from "svelte/elements";
  import {
    CONTROL_HEIGHT,
    CONTROL_OUTLINE,
    FOCUS_RING,
    PILL_FRAME,
    PRESS,
    TAP_AREA,
  } from "./recipes.ts";

  let {
    variant = "subtle",
    size = "md",
    shape = "control",
    loading = false,
    done = false,
    disabled = false,
    join,
    full = false,
    href,
    children,
    ...rest
  }: {
    variant?: "primary" | "subtle" | "ghost" | "danger" | "pill" | "pill-primary";
    size?: "md" | "lg";
    shape?: "control" | "round";
    join?: "start" | "end";
    full?: boolean;
    loading?: boolean;
    done?: boolean;
    href?: string;
    children: Snippet;
  } & HTMLButtonAttributes &
    HTMLAnchorAttributes = $props();

  const link = $derived(href !== undefined);
  const pill = $derived(variant === "pill" || variant === "pill-primary");
  const largePadding = $derived(join === "end" ? "px-3" : shape === "round" ? "px-6" : "px-5");

  const classes = $derived([
    `relative inline-flex items-center gap-1.5 font-medium whitespace-nowrap select-none ${PRESS} ${FOCUS_RING} disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50`,
    full ? "w-full min-w-0 justify-start" : "justify-center",
    pill ? [PILL_FRAME, TAP_AREA] : "text-base",
    variant === "pill" && "bg-surface-2 text-ink-1 hover:bg-surface-3",
    variant === "pill-primary" &&
      "border-transparent bg-accent text-accent-contrast hover:bg-accent-hover",
    !pill && (shape === "round" ? "rounded-pill" : "rounded-control"),
    !pill && size === "md" && [CONTROL_HEIGHT, join === "end" ? "px-1.5" : "px-2.5"],
    !pill && size === "lg" && [shape === "round" ? "h-12 text-lg" : "h-11 text-lg", largePadding],
    join === "start" && "rounded-r-none",
    join === "start" && variant === "subtle" && "border-r-0",
    join === "end" && "rounded-l-none border-l border-current/20",
    variant === "primary" && "bg-accent text-accent-contrast hover:bg-accent-hover",
    variant === "subtle" && [CONTROL_OUTLINE, "text-ink-1"],
    variant === "ghost" && "text-ink-2 hover:bg-surface-2 hover:text-ink-1",
    variant === "danger" && "bg-status-critical text-white hover:bg-status-critical-hover",
  ]);
</script>

{#snippet inner()}
  <span
    class={[
      "inline-flex items-center gap-1.5",
      full && "min-w-0 flex-1",
      (loading || done) && "invisible",
    ]}
  >
    {@render children()}
  </span>
  {#if loading}
    <span class="absolute inset-0 grid animate-late place-items-center">
      <Spinner />
    </span>
  {:else if done}
    <span class="absolute inset-0 grid place-items-center" data-testid="button-done">
      <Icon name="checked" size={14} />
    </span>
  {/if}
{/snippet}

{#if link}
  <a {href} {...rest} aria-busy={loading || undefined} class={classes}>
    {@render inner()}
  </a>
{:else}
  <button
    type="button"
    {...rest}
    disabled={disabled || loading}
    aria-busy={loading || undefined}
    class={classes}
  >
    {@render inner()}
  </button>
{/if}
